import React, { useState, useEffect } from 'react';
import { 
  Database, Users, MapPin, Search, Plus, 
  RefreshCw, Filter, AlertCircle, Check
} from 'lucide-react';
import api from '../services/api';

export default function PoolManager() {
  const [stats, setStats] = useState({
    mernis_total: 0, pool_total: 0, pool_pending: 0, pool_success: 0
  });
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Filtreler
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [gender, setGender] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [limit, setLimit] = useState(1000);
  
  // Önizleme
  const [preview, setPreview] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  
  // Mesaj
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadStats();
    loadCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
      setSelectedDistrict('');
    } else {
      setDistricts([]);
    }
  }, [selectedCity]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/pool-manager/stats');
      if (res.data.success) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const res = await api.get('/pool-manager/cities');
      if (res.data.success) {
        setCities(res.data.cities);
      }
    } catch (err) {
      console.error('Cities error:', err);
    }
  };

  const loadDistricts = async (city) => {
    try {
      const res = await api.get(`/pool-manager/districts?city=${encodeURIComponent(city)}`);
      if (res.data.success) {
        setDistricts(res.data.districts);
      }
    } catch (err) {
      console.error('Districts error:', err);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post('/pool-manager/preview', {
        city: selectedCity,
        district: selectedDistrict,
        gender: gender || null,
        age_min: ageMin || null,
        age_max: ageMax || null,
        limit: 100
      });
      if (res.data.success) {
        setPreview(res.data.preview);
        setPreviewTotal(res.data.total);
      }
    } catch (err) {
      showMessage('error', 'Önizleme hatası!');
    } finally {
      setPreviewing(false);
    }
  };

  const handleAddToQueue = async () => {
    if (!window.confirm(`${limit.toLocaleString()} kayıt havuza eklenecek. Devam?`)) return;
    
    setAdding(true);
    try {
      const res = await api.post('/pool-manager/add-to-queue', {
        city: selectedCity,
        district: selectedDistrict,
        gender: gender || null,
        age_min: ageMin || null,
        age_max: ageMax || null,
        limit: limit
      });
      if (res.data.success) {
        showMessage('success', `${res.data.added.toLocaleString()} kayıt eklendi!`);
        loadStats();
      } else {
        showMessage('error', res.data.error || 'Ekleme hatası!');
      }
    } catch (err) {
      showMessage('error', 'Ekleme hatası!');
    } finally {
      setAdding(false);
    }
  };

  const formatNumber = (num) => num?.toLocaleString('tr-TR') || '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
            <Database className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Havuz Yönetimi</h1>
            <p className="text-gray-400 text-sm">101M veritabanından veri çekme</p>
          </div>
        </div>
        <button onClick={loadStats} className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700">
          <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
          'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Stats - Sadece 4 kart */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-gray-500">Veritabanı (101M)</span>
          </div>
          <div className="text-2xl font-bold text-purple-400">{formatNumber(stats.mernis_total)}</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-500">Havuz Toplam</span>
          </div>
          <div className="text-2xl font-bold text-cyan-400">{formatNumber(stats.pool_total)}</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-gray-500">Bekleyen</span>
          </div>
          <div className="text-2xl font-bold text-yellow-400">{formatNumber(stats.pool_pending)}</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-gray-500">Başarılı</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">{formatNumber(stats.pool_success)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-cyan-400" />
          Veri Filtreleri
        </h2>
        
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
          {/* İl */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">İl</label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
            >
              <option value="">Tümü</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          
          {/* İlçe */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">İlçe</label>
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              disabled={!selectedCity}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500 disabled:opacity-50"
            >
              <option value="">Tümü</option>
              {districts.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          
          {/* Cinsiyet */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Cinsiyet</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
            >
              <option value="">Tümü</option>
              <option value="E">Erkek</option>
              <option value="K">Kadın</option>
            </select>
          </div>
          
          {/* Yaş Min */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Min Yaş</label>
            <input
              type="number"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              placeholder="18"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
          
          {/* Yaş Max */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Max Yaş</label>
            <input
              type="number"
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              placeholder="70"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
          
          {/* Limit */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Eklenecek Adet</label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(Math.min(1000000, parseInt(e.target.value) || 0))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium"
          >
            <Search className="w-4 h-4" />
            {previewing ? 'Yükleniyor...' : 'Önizle'}
          </button>
          
          <button
            onClick={handleAddToQueue}
            disabled={adding}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white font-medium"
          >
            <Plus className="w-4 h-4" />
            {adding ? 'Ekleniyor...' : `Havuza Ekle (${limit.toLocaleString()})`}
          </button>
        </div>
      </div>

      {/* Preview Table */}
      {preview.length > 0 && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="text-white font-medium">
              Önizleme ({preview.length} / {previewTotal.toLocaleString()} kayıt)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">TC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Ad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Soyad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Doğum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">İl</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">İlçe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {preview.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-800/30">
                    <td className="px-4 py-3 font-mono text-sm text-white">{item.tc}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{item.adi}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{item.soyadi}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{item.dogum_tarihi}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{item.il}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{item.ilce}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
